import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Injector, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core'
import { FieldTree } from '@angular/forms/signals'

import { LaskunAlv, LaskunTyyppiavain, Projekti, VerottomanMyynninPeruste } from '@lemon/core-firebase/model/lasku'
import { LASKUN_YKSIKOT } from '@lemon/core-firebase/model/laskun-yksikot'
import { LaskuSharedService } from '@lemon/core-firebase/service/lasku/lasku-shared.service'
import { YksikkoService } from '@lemon/core-firebase/service/lasku/yksikko.service'

import { Observable, Subject } from 'rxjs'
import { auditTime, takeUntil } from 'rxjs/operators'
import { arvonMuutokset, asetaArvo } from '../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { ProjektitService } from '../_jaettu-angular/projektit.service'
import { KuutionTila } from './kuutio-ikoni.component'
import { LaskunRiviLomake } from './lasku-rivit.component'

export interface AlvinLukituksenSelite {
  kaannosavain: string
  myynninLaji: LaskunTyyppiavain
}

export interface FieldError {
  field: string
  error: string
}

type RivinKentta = 'nimi' | 'tarkenne' | 'maara' | 'yksikko' | 'ahinta' | 'alv' | 'ale'

const KENTTIEN_JARJESTYS: RivinKentta[] = ['nimi', 'tarkenne', 'maara', 'yksikko', 'ahinta', 'alv', 'ale']
const ALKUKIRJAINTEN_VALI_MILLIS = 600
const YKSIKON_HAKUVALINTA = '__hae'

@Component({
  selector: 'tbody[app-lasku-rivi]',
  templateUrl: './lasku-rivi.component.html',
  styleUrls: ['./lasku-rivi.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  host: {
    class: 'rt-r',
    '[class.on]': 'riviOnAktiivinen',
    '[class.menu]': 'rivinValikkoOnAuki',
    '[class.drag]': 'riviaRaahataan',
    '[class.over]': 'riviOnRaahauksenKohde',
  },
})
export class LaskuRiviComponent implements OnInit, OnDestroy {
  @ViewChild('nimiInput') nimiInput: ElementRef<HTMLInputElement>
  @ViewChild('tarkenneInput') tarkenneInput: ElementRef<HTMLInputElement>
  @ViewChild('maaraInput') maaraInput: ElementRef<HTMLInputElement>
  @ViewChild('yksikkoSelect') yksikkoSelect: ElementRef<HTMLSelectElement>
  @ViewChild('ahintaInput') ahintaInput: ElementRef<HTMLInputElement>
  @ViewChild('alvSelect') alvSelect: ElementRef<HTMLSelectElement>
  @ViewChild('aleInput') aleInput: ElementRef<HTMLInputElement>

  @Output() riviPoistettuTapahtuma: EventEmitter<FieldTree<LaskunRiviLomake, number>> = new EventEmitter()
  @Output() riviMonistettuTapahtuma: EventEmitter<FieldTree<LaskunRiviLomake, number>> = new EventEmitter()
  @Output() alennusLisattiinTapahtuma: EventEmitter<void> = new EventEmitter()
  @Output() validaatioMuuttuiTapahtuma: EventEmitter<FieldError[]> = new EventEmitter()
  @Output() siirrySeuraavalleRivilleTapahtuma: EventEmitter<void> = new EventEmitter()
  @Output() rivinRaahausAlkoiTapahtuma: EventEmitter<void> = new EventEmitter()
  @Output() rivinRaahausLoppuiTapahtuma: EventEmitter<void> = new EventEmitter()
  @Output() raahattuRiviPudotettiinTalleTapahtuma: EventEmitter<void> = new EventEmitter()

  @Input() alvt: LaskunAlv[] = null
  @Input() alvinLukituksenSelite: AlvinLukituksenSelite | null = null
  @Input() rekisterivalintaKaytossa = true
  @Input() parent: FieldTree<LaskunRiviLomake, number>
  @Input() paivitaValidaatiot: any
  @Input() laskunValuutta: string = null
  @Input() naytaProjektit = false
  @Input() naytaAlennus = false
  @Input() sarakkeita = 8
  @Input() riviaRaahataan = false
  @Input() riviOnRaahauksenKohde = false

  naytettavatProjektitObservable: Observable<Projekti[]>
  rivinYksikkoVaihtoehdot: string[] = LASKUN_YKSIKOT
  yksikonHakuvalinta = YKSIKON_HAKUVALINTA
  yksikonHakuAuki = false
  verottomanMyynninPerusteet = Object.values(VerottomanMyynninPeruste)
  riviOnAktiivinen = false
  rivinValikkoOnAuki = false

  private ngUnsubscribe = new Subject<void>()
  private _yksikonAlkukirjaimet = ''
  private _yksikonEdellinenKirjainAikaleima = 0
  private _viimeisinYksikko: string
  private readonly _injector = inject(Injector)

  constructor(
    private _projektitService: ProjektitService,
    private _elementRef: ElementRef<HTMLElement>,
    private _changeDetectorRef: ChangeDetectorRef,
    private _laskuSharedService: LaskuSharedService,
    private _yksikkoService: YksikkoService,
  ) {}

  ngOnInit() {
    arvonMuutokset(this.parent, { injector: this._injector })
      .pipe(auditTime(100), takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.paivitaVirheet()
        this._changeDetectorRef.markForCheck()
      })

    this.naytettavatProjektitObservable = this._projektitService.naytettavatKirjanpitoprojektitObservable

    const nykyinenYksikko = this.parent.yksikko().value()
    const yksikkoPuuttuuListalta = nykyinenYksikko && !LASKUN_YKSIKOT.includes(nykyinenYksikko)
    if (yksikkoPuuttuuListalta) {
      this.rivinYksikkoVaihtoehdot = [nykyinenYksikko, ...LASKUN_YKSIKOT]
    }

    this._viimeisinYksikko = nykyinenYksikko
    arvonMuutokset(this.parent.yksikko, { injector: this._injector })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(yksikko => this._kasitteleYksikonMuutos(yksikko))
  }

  get kuutionTila(): KuutionTila {
    const onRekisterista = !!this.parent.tuote().value()?.$key
    const tallennetaan = this.rekisterivalintaKaytossa && this.parent.tallennetaanRekisteriin().value()
    if (onRekisterista && tallennetaan) {
      return KuutionTila.REKISTERI_PAIVITETAAN
    }
    if (onRekisterista) {
      return KuutionTila.REKISTERI
    }
    if (tallennetaan) {
      return KuutionTila.VAPAA_TALLENNETAAN
    }
    return KuutionTila.VAPAA
  }

  vaihdaRekisteriinTallennus() {
    if (!this.rekisterivalintaKaytossa) {
      return
    }
    const kentta = this.parent.tallennetaanRekisteriin
    asetaArvo(kentta, !kentta().value())
    kentta().markAsDirty()
  }

  get naytetaankoVerottomuudenPeruste(): boolean {
    return this._laskuSharedService.onkoVerottomuudenPerusteTarpeen(this.parent.alv().value())
  }

  get naytetaankoRivinTarkenne(): boolean {
    return this.riviOnAktiivinen || !!this.parent.tarkenne().value()
  }

  @HostListener('focusin')
  merkitseRiviAktiiviseksi() {
    this.riviOnAktiivinen = true
  }

  @HostListener('focusout', ['$event'])
  merkitseRiviEpaaktiiviseksiJosKohdistusPoistuiRivilta(event: FocusEvent) {
    const kohdistusPysyyRivilla = event.relatedTarget instanceof Node && this._elementRef.nativeElement.contains(event.relatedTarget)
    if (!kohdistusPysyyRivilla) {
      this.riviOnAktiivinen = false
    }
  }

  @HostListener('document:mousedown', ['$event'])
  suljeRivinValikkoKunKlikataanSenUlkopuolelle(event: MouseEvent) {
    const klikkausValikossa = event.target instanceof Element && !!event.target.closest('.rt-dots')
    if (this.rivinValikkoOnAuki && !klikkausValikossa) {
      this.rivinValikkoOnAuki = false
      this._changeDetectorRef.markForCheck()
    }
  }

  @HostListener('dragover', ['$event'])
  salliRaahatunRivinPudotusTalleRiville(event: DragEvent) {
    event.preventDefault()
  }

  @HostListener('drop', ['$event'])
  pudotaRaahattuRiviTamanRivinKohdalle(event: DragEvent) {
    event.preventDefault()
    this.raahattuRiviPudotettiinTalleTapahtuma.next()
  }

  aloitaRivinRaahaus(event: DragEvent) {
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', '')
    this.rivinRaahausAlkoiTapahtuma.next()
  }

  lopetaRivinRaahaus() {
    this.rivinRaahausLoppuiTapahtuma.next()
  }

  avaaTaiSuljeRivinValikko() {
    this.rivinValikkoOnAuki = !this.rivinValikkoOnAuki
  }

  kohdistaRivinNimikenttaan() {
    this.nimiInput?.nativeElement.focus()
  }

  kohdistaRivinTarkennekenttaan() {
    this.riviOnAktiivinen = true
    this._changeDetectorRef.detectChanges()
    this.tarkenneInput?.nativeElement.focus()
  }

  kohdistaRivinAlennuskenttaan() {
    this._changeDetectorRef.detectChanges()
    this.aleInput?.nativeElement.focus()
  }

  kasitteleMaarakentanNappainpainallus(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.siirryRivinSeuraavaanKenttaan('maara', event)
      return
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault()
      this._muutaMaaraaYhdella(event.key === 'ArrowUp' ? 1 : -1)
      return
    }
    const painettiinKirjain = event.key.length === 1 && /[a-zåäö²³]/i.test(event.key)
    if (painettiinKirjain) {
      event.preventDefault()
      this._valitseYksikkoAlkukirjaimilla(event.key)
    }
  }

  private _muutaMaaraaYhdella(suunta: 1 | -1) {
    const maaraKentta = this.parent.maara
    const uusiMaara = Math.round(((maaraKentta().value() ?? 0) + suunta) * 10000) / 10000
    asetaArvo(maaraKentta, uusiMaara)
    maaraKentta().markAsDirty()
  }

  private _valitseYksikkoAlkukirjaimilla(kirjain: string) {
    const alkukirjaimet = this._kokoaYksikonAlkukirjaimet(kirjain)
    const yksikko = this._yksikkoService.haeYksikoita(alkukirjaimet, 1)[0]
    if (yksikko) {
      this._lisaaYksikkoVaihtoehtoihin(yksikko.tunnus)
      asetaArvo(this.parent.yksikko, yksikko.tunnus)
      this.parent.yksikko().markAsDirty()
      this.yksikkoSelect?.nativeElement.focus()
    }
  }

  valitseYksikko(tunnus: string) {
    this._lisaaYksikkoVaihtoehtoihin(tunnus)
    asetaArvo(this.parent.yksikko, tunnus)
    this.parent.yksikko().markAsDirty()
    this.suljeYksikonHaku()
  }

  suljeYksikonHaku() {
    if (!this.yksikonHakuAuki) {
      return
    }
    this.yksikonHakuAuki = false
    this._changeDetectorRef.detectChanges()
    this.yksikkoSelect?.nativeElement.focus()
  }

  private _kasitteleYksikonMuutos(yksikko: string) {
    const valittiinHaku = yksikko === YKSIKON_HAKUVALINTA
    if (!valittiinHaku) {
      this._viimeisinYksikko = yksikko
      return
    }
    asetaArvo(this.parent.yksikko, this._viimeisinYksikko)
    this._avaaYksikonHaku()
  }

  private _avaaYksikonHaku() {
    this.yksikonHakuAuki = true
    this._changeDetectorRef.markForCheck()
  }

  private _lisaaYksikkoVaihtoehtoihin(tunnus: string) {
    const onkoVaihtoehdoissa = this.rivinYksikkoVaihtoehdot.includes(tunnus)
    if (!onkoVaihtoehdoissa) {
      this.rivinYksikkoVaihtoehdot = [...this.rivinYksikkoVaihtoehdot, tunnus]
    }
  }

  private _kokoaYksikonAlkukirjaimet(kirjain: string): string {
    const nyt = Date.now()
    const kirjainJatkaaAlkukirjaimia = nyt - this._yksikonEdellinenKirjainAikaleima < ALKUKIRJAINTEN_VALI_MILLIS
    this._yksikonAlkukirjaimet = (kirjainJatkaaAlkukirjaimia ? this._yksikonAlkukirjaimet : '') + kirjain.toLowerCase()
    this._yksikonEdellinenKirjainAikaleima = nyt
    return this._yksikonAlkukirjaimet
  }

  siirryRivinSeuraavaanKenttaan(nykyinen: RivinKentta, event: Event) {
    event.preventDefault()
    const seuraavat = KENTTIEN_JARJESTYS.slice(KENTTIEN_JARJESTYS.indexOf(nykyinen) + 1)
    for (const kentta of seuraavat) {
      const elementti = this._annaRivinKentanElementti(kentta)
      if (elementti && !elementti.disabled) {
        elementti.focus()
        if (elementti instanceof HTMLInputElement) {
          elementti.select()
        }
        return
      }
    }
    this.siirrySeuraavalleRivilleTapahtuma.next()
  }

  private _annaRivinKentanElementti(kentta: RivinKentta): HTMLInputElement | HTMLSelectElement | null {
    switch (kentta) {
      case 'nimi':
        return this.nimiInput?.nativeElement ?? null
      case 'tarkenne':
        return this.tarkenneInput?.nativeElement ?? null
      case 'maara':
        return this.maaraInput?.nativeElement ?? null
      case 'yksikko':
        return this.yksikkoSelect?.nativeElement ?? null
      case 'ahinta':
        return this.ahintaInput?.nativeElement ?? null
      case 'alv':
        return this.alvSelect?.nativeElement ?? null
      case 'ale':
        return this.aleInput?.nativeElement ?? null
    }
  }

  lisaaAlennus() {
    this.rivinValikkoOnAuki = false
    this.alennusLisattiinTapahtuma.next()
  }

  monistaRivi() {
    this.rivinValikkoOnAuki = false
    this.riviMonistettuTapahtuma.next(this.parent)
  }

  poistaRivi() {
    this.rivinValikkoOnAuki = false
    this.riviPoistettuTapahtuma.next(this.parent)
  }

  merkitseTuotteenNimiKosketetuksi() {
    this.parent.tuote().markAsTouched()
    this.paivitaVirheet()
  }

  valitseProjekti(projektit: Projekti[], avain: string) {
    const projekti = projektit.find(vaihtoehto => vaihtoehto.avain === avain) ?? null
    asetaArvo(this.parent.projekti, projekti)
    this.parent.projekti().markAsDirty()
  }

  valitseAlv(tunniste: string) {
    const alv = this.alvt?.find(vaihtoehto => vaihtoehto.tunniste === tunniste) ?? null
    asetaArvo(this.parent.alv, alv)
    this.parent.alv().markAsDirty()
  }

  valitseVerottomuudenPeruste(peruste: string) {
    const valittuPeruste = (peruste || null) as VerottomanMyynninPeruste | null
    asetaArvo(this.parent.verottomuudenPeruste, valittuPeruste)
    this.parent.verottomuudenPeruste().markAsDirty()
  }

  markAsTouched() {
    this.parent.nimi().markAsTouched()
    this.parent.tuote().markAsTouched()
    this.parent.maara().markAsTouched()
    this.parent.yksikko().markAsTouched()
    this.parent.ahinta().markAsTouched()
    this.parent.alv().markAsTouched()
    this.parent.verottomuudenPeruste().markAsTouched()
    this._changeDetectorRef.markForCheck()
  }

  paivitaVirheet() {
    this.validaatioMuuttuiTapahtuma.next(this.annaVirheet())
  }

  private annaVirheet(): FieldError[] {
    const errors: FieldError[] = []
    this.lisaaVirheet(errors, 'tuote')
    this.lisaaVirheet(errors, 'tarkenne')
    this.lisaaVirheet(errors, 'maara')
    this.lisaaVirheet(errors, 'yksikko')
    this.lisaaVirheet(errors, 'ahinta')
    this.lisaaVirheet(errors, 'alv')
    this.lisaaVirheet(errors, 'ale')
    this.lisaaVirheet(errors, 'yhteensa')
    this.lisaaVirheet(errors, 'projekti')
    this.lisaaVirheet(errors, 'verottomuudenPeruste')
    return errors
  }

  private lisaaVirheet(errors: FieldError[], identifier: keyof LaskunRiviLomake) {
    const field = this.parent[identifier]()
    if (field.errors().length > 0 && (field.touched() || field.dirty())) {
      for (const error of field.errors()) {
        errors.push({
          field: identifier,
          error: error.kind,
        })
      }
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }
}
